import { useEffect, useMemo, useState } from 'react'
import { Button, Card, Divider, Form, Input, Modal, Select, Space, Typography } from 'antd'
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton } from '@/widgets/demo/ListPrimitives'
import { useAppNavigate } from '../../hooks/useAppNavigate'
import { applyApiFieldErrors, isHandledAuthError, municipalitiesApi, usersApi } from '@/services'
import { useToast } from '@/widgets/common'
import { useAuth } from '@/hooks'

const { Title, Text } = Typography

import type { UserRole } from '@/constants'
import { USER_ROLE_KEYS, enumLabel } from '@/constants/enums'

const LOGIN_ID_PATTERN = /^[A-Za-z0-9_.@-]{1,50}$/
const LOGIN_ID_ERROR_MESSAGE = 'ログインIDは半角英数字および「_」「-」「.」「@」のみで入力してください。'

type UserCreateFormValues = {
  id: string
  municipalityId: number | null
  name: string
  dept: string
  email: string
  role: UserRole
  usageStartDate: string
  usageEndDate: string
  status: string
}

const allRoleOptions = USER_ROLE_KEYS.map((key) => ({
  value: key,
  label: enumLabel('roles', key),
}))

const ACTIVE_LABEL = '有効'
const INACTIVE_LABEL = '無効'

const todayInputValue = () => {
  const date = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const deriveStatusLabel = (usageStartDate?: string, usageEndDate?: string) => {
  const today = todayInputValue()
  if (usageStartDate && usageStartDate > today) return INACTIVE_LABEL
  if (usageEndDate && usageEndDate < today) return INACTIVE_LABEL
  return ACTIVE_LABEL
}

const requiredLabel = (label: string) => (
  <span className="inline-flex items-center gap-1">
    <span aria-hidden className="text-sm font-bold text-red-500">
      *
    </span>
    <span>{label}</span>
  </span>
)

const UserCreate = () => {
  const navigate = useAppNavigate()
  const toast = useToast()
  const { session } = useAuth()
  const [form] = Form.useForm<UserCreateFormValues>()
  const [municipalityOptions, setMunicipalityOptions] = useState<{ value: number; label: string }[]>([])
  const [municipalityLoading, setMunicipalityLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const isSystemAdmin = session?.role === 'SYSTEM_ADMIN'

  const roleOptions = useMemo(() => {
    return isSystemAdmin
      ? allRoleOptions
      : allRoleOptions.filter((option) => option.value !== 'SYSTEM_ADMIN')
  }, [isSystemAdmin])

  const ownMunicipalityLabel = useMemo(() => {
    if (session?.municipalityId == null) return ''
    const match = municipalityOptions.find((option) => option.value === session.municipalityId)
    return match?.label ?? ''
  }, [municipalityOptions, session])

  const fillLoginIdFromEmail = () => {
    const rawEmail = form.getFieldValue('email')
    const email = typeof rawEmail === 'string' ? rawEmail.trim() : ''
    if (!email) {
      toast.warning('先にメールアドレスを入力してください。')
      return
    }
    const atIndex = email.indexOf('@')
    const localPart = atIndex >= 0 ? email.slice(0, atIndex) : email
    if (!localPart) {
      toast.warning('メールアドレスの「@」より前の部分が空です。')
      return
    }
    if (localPart.length > 50) {
      toast.warning('メールアドレスの「@」より前が50文字を超えています。手動で入力してください。')
      return
    }
    if (!LOGIN_ID_PATTERN.test(localPart)) {
      toast.warning('メールアドレスの「@」より前にログインIDで使用できない文字が含まれています。')
      return
    }
    form.setFieldValue('id', localPart)
    void form.validateFields(['id']).catch(() => {})
  }

  useEffect(() => {
    // 自治体一覧を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMunicipalityLoading(true)
    municipalitiesApi
      .list()
      .then(({ data }) => {
        const options = data.map((municipality) => ({
          value: municipality.id,
          label: `${municipality.code} ${municipality.name}`,
        }))
        setMunicipalityOptions(options)
        const fallback = options[0]?.value ?? null
        const ownId = session?.municipalityId ?? null
        const initialId = !isSystemAdmin && ownId != null
          ? ownId
          : fallback
        form.setFieldsValue({ municipalityId: initialId })
      })
      .catch(() => {
        setMunicipalityOptions([])
        form.setFieldsValue({ municipalityId: !isSystemAdmin ? session?.municipalityId ?? null : null })
      })
      .finally(() => {
        setMunicipalityLoading(false)
      })
  }, [form, isSystemAdmin, session?.municipalityId])

  const register = async () => {
    if (submitting) return

    let values: UserCreateFormValues
    try {
      values = await form.validateFields()
    } catch (validationError: unknown) {
      if (validationError && typeof validationError === 'object' && 'errorFields' in validationError) return
      Modal.error({ content: '入力内容を確認してください。' })
      return
    }

    if (!values.municipalityId) {
      Modal.error({ content: '自治体情報が取得できていません。' })
      return
    }

    setSubmitting(true)
    try {
      await usersApi.create({
        loginId: values.id.trim(),
        name: values.name.trim(),
        department: values.dept?.trim() || undefined,
        email: values.email?.trim() || undefined,
        role: values.role,
        usageStartDate: values.usageStartDate,
        usageEndDate: values.usageEndDate?.trim() || null,
        municipalityId: values.municipalityId,
      })
      toast.success('ユーザを登録しました。')
      navigate('user-management')
    } catch (error) {
      if (isHandledAuthError(error)) return
      applyApiFieldErrors(form, error, {
        loginId: 'id',
        department: 'dept',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={[
          { label: 'システム管理', page: 'submenu-admin' },
          { label: 'ユーザ管理', page: 'user-management' },
          { label: '新規ユーザ作成' },
        ]}
      />
      <div className="mx-auto max-w-2xl px-6 py-8">
        <BackButton onClick={() => navigate('user-management')} />
        <Card>
          <Title level={4}>新規ユーザ作成</Title>
          <Text type="secondary">必須項目を入力し、「登録」を押してください。</Text>
          <Divider />

          <Form<UserCreateFormValues>
            form={form}
            layout="vertical"
            requiredMark={false}
            className="customer-validation-form"
            initialValues={{
              id: '',
              municipalityId: null,
              name: '',
              dept: '',
              email: '',
              role: 'BUSINESS_CENTER',
              usageStartDate: todayInputValue(),
              usageEndDate: '9999-12-31',
              status: ACTIVE_LABEL,
            }}
            onValuesChange={(changed, allValues) => {
              if ('usageStartDate' in changed || 'usageEndDate' in changed) {
                const nextStatus = deriveStatusLabel(allValues.usageStartDate, allValues.usageEndDate)
                if (allValues.status !== nextStatus) {
                  form.setFieldValue('status', nextStatus)
                }
              }
            }}
          >
            <Form.Item
              name="id"
              label={requiredLabel('ログインID')}
              normalize={(value) => (typeof value === 'string' ? value.trim() : value)}
              rules={[
                { required: true, message: 'ログインIDは必須です。' },
                { pattern: LOGIN_ID_PATTERN, message: LOGIN_ID_ERROR_MESSAGE },
              ]}
              extra={
                <Button type="link" size="small" onClick={fillLoginIdFromEmail} style={{ padding: 0, height: 'auto' }}>
                  メールアドレスの@前を反映
                </Button>
              }
            >
              <Input maxLength={50} style={{ fontFamily: 'monospace' }} placeholder="user001" />
            </Form.Item>

            {isSystemAdmin ? (
              <Form.Item
                name="municipalityId"
                label={requiredLabel('自治体')}
                rules={[{ required: true, message: '自治体は必須です。' }]}
              >
                <Select loading={municipalityLoading} options={municipalityOptions} placeholder="自治体を選択" />
              </Form.Item>
            ) : (
              <>
                <Form.Item name="municipalityId" hidden>
                  <Input />
                </Form.Item>
                <Form.Item label={requiredLabel('自治体')}>
                  <Input value={ownMunicipalityLabel} readOnly disabled />
                </Form.Item>
              </>
            )}

            <Form.Item
              name="name"
              label={requiredLabel('ログインユーザ名')}
              rules={[
                { required: true, message: 'ログインユーザ名は必須です。' },
                { max: 128, message: 'ログインユーザ名は128文字以内で入力してください。' },
              ]}
            >
              <Input maxLength={128} />
            </Form.Item>

            <Form.Item name="dept" label="所属" rules={[{ max: 128, message: '所属は128文字以内で入力してください。' }]}>
              <Input maxLength={128} />
            </Form.Item>

            <Form.Item
              name="email"
              label={requiredLabel('メールアドレス')}
              rules={[
                { required: true, whitespace: true, message: 'メールアドレスを入力してください。' },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') return Promise.resolve()
                    if (value.length > 254) return Promise.reject('メールアドレスは254文字以内で入力してください。')
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return Promise.reject('有効なメールアドレスを入力してください。')
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <Input maxLength={254} placeholder="user@example.com" />
            </Form.Item>

            <Form.Item name="role" label="権限">
              <Select options={roleOptions} />
            </Form.Item>

            <Form.Item
              name="usageStartDate"
              label={requiredLabel('利用開始日')}
              rules={[{ required: true, message: '利用開始日は必須です。' }]}
            >
              <Input type="date" min={todayInputValue()} max="9999-12-31" />
            </Form.Item>

            <Form.Item
              name="usageEndDate"
              label={requiredLabel('利用終了日')}
              dependencies={['usageStartDate']}
              rules={[
                { required: true, message: '利用終了日は必須です。' },
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const start = getFieldValue('usageStartDate')
                    if (!value || !start || value >= start) return Promise.resolve()
                    return Promise.reject('利用終了日は利用開始日以降の日付を指定してください。')
                  },
                }),
              ]}
            >
              <Input type="date" min="1900-01-01" max="9999-12-31" />
            </Form.Item>

            <Form.Item name="status" label="ステータス">
              <Input readOnly disabled />
            </Form.Item>
          </Form>
        </Card>

        <div className="mt-4 flex justify-end">
          <Space>
            <Button icon={<ArrowLeftOutlined />} disabled={submitting} onClick={() => navigate('user-management')}>
              キャンセル
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={municipalityLoading || submitting}
              disabled={municipalityLoading}
              onClick={() => void register()}
            >
              登録
            </Button>
          </Space>
        </div>
      </div>
    </div>
  )
}

export default UserCreate
